import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/products/shared/services/supabase/client';
import { useAuth } from '@/products/shared/state/auth-context';
import type { ProductFlag } from '@/products/shared/utils/feature-access';

interface TenantProductConfig {
  [key: string]: boolean;
}

/**
 * Hook to fetch tenant-specific product configuration from tenant_config table
 * Returns a map of product flags to their enabled status
 */
export const useTenantProductConfig = () => {
  const { tenantId } = useAuth();

  return useQuery<TenantProductConfig, Error>({
    queryKey: ['tenant-product-config', tenantId],
    queryFn: async () => {
      if (!tenantId) {
        return {};
      }

      // Fetch all product enablement configs for this tenant
      const productKeys: ProductFlag[] = [
        'email-analytics',
        'email-analytics-v2',
        'penny',
        'new-payroll-testing',
        'penny2',
        'chat',
        'compliance',
        'integrations',
      ];

      // Build config keys like "product_email_analytics_enabled"
      const configKeys = productKeys.map(
        (product) => `product_${product.replace(/-/g, '_')}_enabled`
      );

      // Build a map of product flags to enabled status
      const configMap: TenantProductConfig = {};

      // Initialize all products as disabled by default
      productKeys.forEach((product) => {
        configMap[product] = false;
      });

      try {
        // Helper to add timeout to a promise
        const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
          return Promise.race([
            promise,
            new Promise<T>((_, reject) => {
              setTimeout(() => reject(new Error(`Query timeout after ${timeoutMs}ms`)), timeoutMs);
            })
          ]);
        };

        // First, check if tenant_config table has ANY entries for this tenant (not just product configs)
        const { data: tenantConfigsCheck, error: checkError } = await withTimeout(
          supabase
            .from('tenant_config')
            .select('id')
            .eq('tenant_id', tenantId)
            .limit(1),
          10000 // 10 second timeout
        );

        const hasAnyConfigs = !checkError && tenantConfigsCheck && tenantConfigsCheck.length > 0;

        // Fetch all configs for this tenant (avoid PostgREST PGRST120: embedded resources
        // can only be filtered with is.null/not.is.null - filter product keys client-side)
        const { data, error } = await withTimeout(
          supabase
            .from('tenant_config')
            .select('config_value, tenant_config_enums!inner(config_key)')
            .eq('tenant_id', tenantId)
            .eq('is_active', true),
          10000 // 10 second timeout
        );

        if (error) {
          console.warn('Error fetching tenant product config:', error);
          // Continue to fallback logic
        } else {
          // Override with values from database if product configs exist
          if (data && data.length > 0) {
            data.forEach((config: any) => {
              const configKey = config.tenant_config_enums?.config_key;
              if (!configKey || !configKeys.includes(configKey)) return;

              const productKey = configKey.replace('product_', '').replace('_enabled', '');
              const productFlag = productKey.replace(/_/g, '-') as ProductFlag;
              
              // Get boolean value from JSONB
              const value = config.config_value;
              if (typeof value === 'boolean') {
                configMap[productFlag] = value;
              } else if (typeof value === 'string') {
                configMap[productFlag] = value.toLowerCase() === 'true';
              } else if (typeof value === 'number') {
                configMap[productFlag] = value !== 0;
              } else if (value === null || value === undefined) {
                configMap[productFlag] = false;
              }
            });
          }

          // Fallback to environment variables ONLY if tenant_config table is completely empty for this tenant
          // If table has any configs (even non-product ones), respect them and don't override with env vars
          if (!hasAnyConfigs) {
            console.log('[useTenantProductConfig] No tenant configs found for this tenant, falling back to environment variables');
            productKeys.forEach((product) => {
              const envKey = `VITE_PRODUCT_${product.replace(/-/g, '_').toUpperCase()}_ENABLED`;
              let envValue = import.meta.env[envKey] as string | boolean | undefined;
              if (product === 'chat' && envValue === undefined) {
                envValue = import.meta.env.VITE_USE_CHAT ?? import.meta.env.vite_use_chat;
              }
              if (envValue !== undefined) {
                const isEnabled = String(envValue).toLowerCase() === 'true';
                if (isEnabled) {
                  console.log(
                    `[useTenantProductConfig] Using fallback env var ${envKey}` +
                      (product === 'chat' && !import.meta.env[envKey] ? ' (or VITE_USE_CHAT)' : '') +
                      ` for product ${product}`
                  );
                  configMap[product] = true;
                }
              } else {
                console.log(`[useTenantProductConfig] Env var ${envKey} not found for product ${product}`);
              }
            });
          } else {
            console.log('[useTenantProductConfig] Tenant has configs in tenant_config table, not using env var fallback');
          }
        }
      } catch (err) {
        console.error('Error or timeout fetching tenant product config:', err);
        // On error/timeout, fallback to environment variables to allow app to continue
        console.log('[useTenantProductConfig] Falling back to environment variables due to error/timeout');
        productKeys.forEach((product) => {
          const envKey = `VITE_PRODUCT_${product.replace(/-/g, '_').toUpperCase()}_ENABLED`;
          let envValue = import.meta.env[envKey] as string | boolean | undefined;
          if (product === 'chat' && envValue === undefined) {
            envValue = import.meta.env.VITE_USE_CHAT ?? import.meta.env.vite_use_chat;
          }
          if (envValue !== undefined) {
            const isEnabled = String(envValue).toLowerCase() === 'true';
            if (isEnabled) {
              console.log(`[useTenantProductConfig] Using fallback env var ${envKey} for product ${product}`);
              configMap[product] = true;
            }
          }
        });
      }

      return configMap;
    },
    enabled: !!tenantId, // Only fetch if tenantId is available
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 1, // Only retry once on failure
    retryDelay: 1000, // Wait 1 second before retry
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};
